import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef, useMemo } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Container, 
  Grid, 
  Card, 
  CardContent,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  TextField,
  Stack,
  Chip,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Alert,
  Paper,
  Divider,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { gql, useQuery } from '@apollo/client';
import { ChevronRight, InfoOutlined, LocationOnOutlined } from '@mui/icons-material';
import './styles.scss';
import { dmaData } from '../../utils/minified-dma-data';
import { dmaInfoMap } from '../../utils/dma-info';
import { useUserContext } from '../../hooks/userContext';

const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  'AIzaSyA3OEL-k1KpEuLj9anC9i-ZivP3ODeVthw'; // fallback to existing key used elsewhere

const GET_DESIGNER_DMAS = gql`
  query GetAllDesignersForDMA {
    getAllDesigners {
      _id
      workLocation
    }
  }
`;

// Types based on backend schema
export interface PreferencesQuizResult {
  social: {
    pronouns?: string;
    pets?: string[];
    lgbtqPositive?: boolean;
    womenOwned?: boolean;
    minorityOwned?: boolean;
    preferToWorkWithRace?: string;
    politicalAffiliation?: string;
    otherPoliticalAffiliation?: string;
    preferSamePolitics?: boolean | null;
  };
  partnershipStyleRankings: { partnershipStyle: string; ranking: number }[];
  acquisitionPreferences: string[];
  acquisitionRankings: { acquisitionPreference: string; ranking: number }[];
  ageRange?: string;
  children?: boolean;
  zipCodes: string[];
}

type ZipEntry = {
  zip: string;
  city?: string;
  cityOptions?: string[];
  dma?: string;
};

type DmaZipOption = {
  zip: string;
  dma: string;
};

const SearchAdornment = () => (
  <InputAdornment position="start">
    <SearchIcon fontSize="small" color="action" />
  </InputAdornment>
);

export interface PreferencesQuizRef {
  nextStep: () => void;
  backStep: () => void;
  getZipCodes: () => string;
  hasZipCodes: () => boolean;
  getCurrentResult: () => PreferencesQuizResult | null;
  handleBegin: () => void;
}

export interface PreferencesQuizProps {
  onComplete?: () => void;
  onResultChange?: (result: PreferencesQuizResult | null) => void;
  onBeginShown?: () => void;
  onQuizStarted?: () => void;
  onStepChange?: (step: number, totalSteps: number) => void;
  onZipCodesChange?: (hasZipCodes: boolean) => void;
  initialData?: PreferencesQuizResult | null;
}

// Data from backend enums
const pronounOptions = [
  { value: 'heHim', label: 'He/Him' },
  { value: 'sheHer', label: 'She/Her' },
  { value: 'theyThem', label: 'They/Them' }
];

const petOptions = [
  { value: 'dog', label: 'I live(d) with a dog' },
  { value: 'cat', label: 'I live(d) with a cat' },
  { value: 'fish', label: 'I live(d) with fish or aquatic pets' },
  { value: 'none', label: "I prefer not to design for homes with pets" }
];

const politicalAffiliations = [
  { value: '', label: 'Please choose one' },
  { value: 'republican', label: 'Republican' },
  { value: 'democrat', label: 'Democrat' },
  { value: 'libertarian', label: 'Libertarian' },
  { value: 'independent', label: 'Independent' },
  { value: 'other', label: 'Other' }
];

const racePreferenceOptions = [
  { value: '', label: 'Please choose one' },
  { value: 'Asian', label: 'Asian' },
  { value: 'BlackOrAfricanAmerican', label: 'Black or African American' },
  { value: 'Caucasian', label: 'Caucasian' },
  { value: 'PacificIslander', label: 'Pacific Islander' },
  { value: 'LatinxOrHispanic', label: 'Latinx or Hispanic' },
  { value: 'NativeAmerican', label: 'Native American' },
  { value: 'Indian', label: 'Indian' },
  { value: 'noPreference', label: 'Prefer not to say' }
];

const partnershipStyles = [
  { value: 'lowInvolvement', label: 'Low Involvement', description: 'Minimal client interaction, designer-led decisions' },
  { value: 'collaborative', label: 'Collaborative', description: 'Balanced partnership with shared decision-making' },
  { value: 'highlyInvolved', label: 'Highly Involved', description: 'Frequent client interaction and input' }
];

const acquisitionOptions = [
  { value: 'designerControlled', label: 'Designer Controlled', description: 'I select & buy with client approval' },
  { value: 'clientContribution', label: 'Client Contribution', description: 'Ok with the client shopping for a better price and/or buying on their own' },
  { value: 'hybrid', label: 'Hybrid', description: 'Ok with a hybrid approach of some me and some client purchases' }
];

const ageRanges = [
  { value: 'underTwentyFive', label: 'Under 25' },
  { value: 'twentyFiveToThirtyFive', label: '25-35' },
  { value: 'thirtyFiveToFortyFive', label: '35-45' },
  { value: 'fortyFiveToFiftyFive', label: '45-55' },
  { value: 'fiftyFiveToSixtyFive', label: '55-65' },
  { value: 'sixtyFiveAndUp', label: '65+' }
];

const allowedPetValues = petOptions.map((p) => p.value);

const normalizePetSelection = (value?: string) => {
  if (!value) return undefined;
  return allowedPetValues.includes(value) ? value : undefined;
};

const normalizeMinorityOwned = (value: any): boolean | null => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const trimmed = value.trim().toLowerCase();
    if (trimmed === '') return null;
    if (trimmed === 'true') return true;
    if (trimmed === 'false') return false;
    if (trimmed === 'n/a') return null;
    // Legacy string values implied a positive response
    return true;
  }
  return null;
};

export const PreferencesQuiz = forwardRef<PreferencesQuizRef, PreferencesQuizProps>(({
  onComplete,
  onResultChange,
  onBeginShown,
  onQuizStarted,
  onStepChange,
  onZipCodesChange,
  initialData
}, ref) => {
  const [showBegin, setShowBegin] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 5;

  // Step data - initialize from initialData if provided
  const [pronouns, setPronouns] = useState<string>(
    initialData?.social?.pronouns || ''
  );
  // Handle backward compatibility: if initialData has array (old format), take first value
  const getInitialPet = (): string | undefined => {
    if (!initialData?.social?.pets) return undefined;
    if (Array.isArray(initialData.social.pets)) {
      return normalizePetSelection(initialData.social.pets[0] || undefined);
    }
    // If data came through as a single string, normalize and use it
    return normalizePetSelection(initialData.social.pets as unknown as string);
  };
  const [selectedPet, setSelectedPet] = useState<string | undefined>(
    getInitialPet()
  );
  const [lgbtqPositive, setLgbtqPositive] = useState<boolean | null>(
    initialData?.social?.lgbtqPositive ?? null
  );
  const [womenOwned, setWomenOwned] = useState<boolean | null>(
    initialData?.social?.womenOwned ?? null
  );
  const [minorityOwned, setMinorityOwned] = useState<boolean | null>(
    normalizeMinorityOwned(initialData?.social?.minorityOwned)
  );
  const [preferToWorkWithRace, setPreferToWorkWithRace] = useState<string>(
    initialData?.social?.preferToWorkWithRace || ''
  );
  const [politicalAffiliation, setPoliticalAffiliation] = useState<string>(
    initialData?.social?.politicalAffiliation || ''
  );
  const [otherPoliticalAffiliation, setOtherPoliticalAffiliation] = useState<string>(
    initialData?.social?.otherPoliticalAffiliation || ''
  );
  const [preferSamePolitics, setPreferSamePolitics] = useState<boolean | null>(
    initialData?.social?.preferSamePolitics ?? null
  );
  
  const [partnershipStylePreferences, setPartnershipStylePreferences] = useState<string[]>(
    initialData?.partnershipStyleRankings?.map(r => r.partnershipStyle) || []
  );
  const [acquisitionPreferences, setAcquisitionPreferences] = useState<string[]>(
    initialData?.acquisitionPreferences || []
  );
  const [ageRange, setAgeRange] = useState<string>(
    initialData?.ageRange || ''
  );
  const [hasChildren, setHasChildren] = useState<boolean>(
    initialData?.children ?? false
  );
  const { user } = useUserContext();
  const isDesigner = (user?.accountType || "").toLowerCase() === "designer";
  const currentUserId = (user as any)?._id || user?.id;
  const { data: designerDmaData, loading: designerDmaLoading } = useQuery(GET_DESIGNER_DMAS, {
    skip: !currentUserId || !isDesigner,
    fetchPolicy: 'cache-first',
  });

  const dbAssignedDmas = useMemo<string[]>(() => {
    const list = designerDmaData?.getAllDesigners || [];
    const userId = currentUserId;
    const match = list.find((d: any) => (d?._id || d?.id) === userId);
    const wl = (match?.workLocation || []) as (string | number | null | undefined)[];
    return wl.filter(Boolean).map((dma) => String(dma));
  }, [designerDmaData, currentUserId]);

  const assignedDmas = useMemo<string[]>(() => {
    if (dbAssignedDmas.length > 0) return dbAssignedDmas;
    const wl =
      (user as { workLocation?: (string | number | null | undefined)[] } | null)
        ?.workLocation || [];
    return wl.filter(Boolean).map((dma) => String(dma));
  }, [dbAssignedDmas, user]);
  const assignedDmaSet = useMemo(() => new Set(assignedDmas), [assignedDmas]);

  const parseZipEntry = (value: string): ZipEntry => {
    if (!value) return { zip: "" };
    const [zipPart, cityPart, dmaPart] = value.split("::");
    return { zip: zipPart, city: cityPart || undefined, dma: dmaPart || undefined };
  };

  const [zipEntries, setZipEntries] = useState<ZipEntry[]>(
    (initialData?.zipCodes || []).map(parseZipEntry).filter((z) => z.zip)
  );
  const [zipCodeInput, setZipCodeInput] = useState<string>(''); // repurposed as search input

  const [isCompleted, setIsCompleted] = useState(false);
  const [zipLookupStatus, setZipLookupStatus] = useState<Record<string, 'loading' | 'loaded' | 'error'>>({});
  const [zipCityMap, setZipCityMap] = useState<Record<string, { cityOptions: string[]; dma?: string }>>({});

  const getDmaForZip = useCallback((zip: string): string | undefined => {
    const lookup = (dmaData as any)?.[zip];
    const code = lookup?.DMA ?? lookup?.dma;
    return code ? String(code) : undefined;
  }, []);

  const formatDmaLabel = useCallback((dma?: string) => {
    if (!dma) return "";
    const info = dmaInfoMap[dma];
    return info?.name ? `DMA ${dma} - ${info.name}` : `DMA ${dma}`;
  }, []);

  const dmaZipOptions = useMemo<DmaZipOption[]>(() => {
    if (assignedDmaSet.size === 0) return [];
    const options: DmaZipOption[] = [];
    Object.entries(dmaData as Record<string, any>).forEach(([zip, info]) => {
      const code = info?.DMA ?? info?.dma;
      if (code && assignedDmaSet.has(String(code))) {
        options.push({ zip, dma: String(code) });
      }
    });
    return options;
  }, [assignedDmaSet]);

  const filteredOptions = useMemo(() => {
    const term = zipCodeInput.trim().toLowerCase();
    const enriched = dmaZipOptions.map((opt) => {
      const meta = zipCityMap[opt.zip];
      const city = meta?.cityOptions?.[0];
      return { ...opt, city };
    });
    if (!term) return enriched;
    return enriched.filter((opt) => {
      const cityMatch = opt.city?.toLowerCase().includes(term);
      return cityMatch || opt.zip.includes(term) || opt.dma.includes(term);
    });
  }, [dmaZipOptions, zipCityMap, zipCodeInput]);
  const visibleOptions = useMemo(() => filteredOptions.slice(0, 200), [filteredOptions]);

  // Navigation methods
  const handleNext = useCallback(() => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, totalSteps]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const handleBegin = () => {
    setShowBegin(false);
    if (onQuizStarted) onQuizStarted();
  };

  const fetchCitiesForZip = useCallback(async (zip: string, dmaCode?: string) => {
    if (!zip) return;
    setZipLookupStatus((prev) => ({ ...prev, [zip]: 'loading' }));
    try {
      const citySet = new Set<string>();
      const addCity = (city?: string) => {
        const normalized = (city || '').trim();
        if (normalized) {
          citySet.add(normalized);
        }
      };

      // Primary: Zippopotam (fast, no key)
      const response = await fetch(`https://api.zippopotam.us/us/${encodeURIComponent(zip)}`);
      if (response.ok) {
        const data = await response.json();
        (data.places || [])
          .map((p: any) => p?.['place name'])
          .filter(Boolean)
          .forEach((c: any) => addCity(String(c)));
      }

      // Fallback/augment: Google Geocoding API (has richer locality data)
      if (citySet.size <= 1 && GOOGLE_MAPS_API_KEY) {
        const geoRes = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
            zip
          )}&components=country:US&key=${GOOGLE_MAPS_API_KEY}`
        );
        if (geoRes.ok) {
          const geoJson = await geoRes.json();
          (geoJson.results || []).forEach((result: any) => {
            (result.address_components || []).forEach((comp: any) => {
              if (
                comp.types?.includes('locality') ||
                comp.types?.includes('sublocality') ||
                comp.types?.includes('postal_town')
              ) {
                if (comp.long_name) {
                  addCity(String(comp.long_name));
                }
              }
            });
            // Some postal_code results include alternate city names here
            if (Array.isArray(result.postcode_localities)) {
              result.postcode_localities.forEach((locality: string) => addCity(locality));
            }
          });
        }
      }

      const cities: string[] = Array.from(citySet);
      setZipCityMap((prev) => ({
        ...prev,
        [zip]: {
          cityOptions: cities,
          dma: dmaCode || getDmaForZip(zip) || prev[zip]?.dma,
        },
      }));
      setZipEntries((prev) =>
        prev.map((entry) =>
          entry.zip === zip
            ? {
                ...entry,
                dma: entry.dma || dmaCode || getDmaForZip(zip),
                cityOptions: cities,
                city: entry.city && cities.includes(entry.city) ? entry.city : cities[0],
              }
            : entry
        )
      );
      setZipLookupStatus((prev) => ({ ...prev, [zip]: 'loaded' }));
    } catch (error) {
      setZipLookupStatus((prev) => ({ ...prev, [zip]: 'error' }));
    }
  }, [getDmaForZip]);

  // Prefetch city options for any zip entries missing data
  useEffect(() => {
    zipEntries.forEach((entry) => {
      if (
        entry.zip &&
        !zipCityMap[entry.zip]?.cityOptions &&
        zipLookupStatus[entry.zip] !== 'loading' &&
        zipLookupStatus[entry.zip] !== 'error'
      ) {
        fetchCitiesForZip(entry.zip, entry.dma || getDmaForZip(entry.zip));
      }
    });
  }, [zipEntries, zipLookupStatus, fetchCitiesForZip, getDmaForZip, zipCityMap]);

  // Prefetch city names for currently visible options to show city labels
  useEffect(() => {
    const toPrefetch = visibleOptions; // fetch for all currently visible rows
    toPrefetch.forEach((opt) => {
      if (
        !zipCityMap[opt.zip]?.cityOptions &&
        zipLookupStatus[opt.zip] !== 'loading' &&
        zipLookupStatus[opt.zip] !== 'error'
      ) {
        fetchCitiesForZip(opt.zip, opt.dma);
      }
    });
  }, [visibleOptions, zipCityMap, zipLookupStatus, fetchCitiesForZip]);

  // Expose navigation methods to parent
  const zipEntriesToStrings = useCallback(
    (entries: ZipEntry[]) =>
      entries.map((entry) =>
        {
          const city = entry.city || zipCityMap[entry.zip]?.cityOptions?.[0];
          const dma = entry.dma || zipCityMap[entry.zip]?.dma;
          const parts = [entry.zip];
          if (city) parts.push(city);
          if (dma) parts.push(dma);
          return parts.join("::");
        }
      ),
    [zipCityMap]
  );

  useImperativeHandle(ref, () => ({
    nextStep: handleNext,
    backStep: handleBack,
    getZipCodes: () => zipEntriesToStrings(zipEntries).join(', '),
    hasZipCodes: () => zipEntries.length > 0,
    handleBegin,
    getCurrentResult: () => {
      const normalizedPet = normalizePetSelection(selectedPet);
      const normalizedMinorityOwned = minorityOwned === null ? undefined : minorityOwned;

      const result: PreferencesQuizResult = {
        social: {
          pronouns: pronouns || undefined,
          pets: normalizedPet ? [normalizedPet] : undefined,
          lgbtqPositive: lgbtqPositive ?? undefined,
          womenOwned: womenOwned ?? undefined,
          minorityOwned: normalizedMinorityOwned,
          preferToWorkWithRace: preferToWorkWithRace || undefined,
          politicalAffiliation: politicalAffiliation || undefined,
          otherPoliticalAffiliation: (politicalAffiliation === 'other' && otherPoliticalAffiliation) ? otherPoliticalAffiliation : undefined,
          preferSamePolitics: preferSamePolitics ?? undefined
        },
        partnershipStyleRankings: partnershipStylePreferences.map((style, index) => ({
          partnershipStyle: style,
          ranking: index + 1
        })),
        acquisitionPreferences: acquisitionPreferences,
        acquisitionRankings: acquisitionPreferences.map((pref, index) => ({
          acquisitionPreference: pref,
          ranking: index + 1
        })),
        ageRange: ageRange || undefined,
        children: hasChildren,
        zipCodes: zipEntriesToStrings(zipEntries)
      };
      return result;
    }
  }), [handleNext, handleBack, handleBegin, zipEntriesToStrings, zipEntries, pronouns, selectedPet, lgbtqPositive, womenOwned, minorityOwned, preferToWorkWithRace, politicalAffiliation, otherPoliticalAffiliation, preferSamePolitics, partnershipStylePreferences, acquisitionPreferences, ageRange, hasChildren]);

  // Notify parent about step changes
  useEffect(() => {
    if (!showBegin && onStepChange) {
      onStepChange(currentStep, totalSteps);
    }
  }, [currentStep, showBegin, onStepChange, totalSteps]);

  // Check completion status - only mark as completed when user has actually finished all steps
  useEffect(() => {
    const completed = !showBegin && currentStep >= totalSteps;
    if (completed !== isCompleted) {
      setIsCompleted(completed);
      if (completed && onComplete) {
        onComplete();
      }
    }
  }, [currentStep, showBegin, isCompleted, onComplete, totalSteps]);

  // Notify parent when zip codes change
  useEffect(() => {
    if (onZipCodesChange) {
      onZipCodesChange(zipEntries.length > 0);
    }
  }, [zipEntries, onZipCodesChange]);

  // Update result when data changes
  useEffect(() => {
    if (!showBegin && onResultChange) {
      const normalizedPet = normalizePetSelection(selectedPet);
      const normalizedMinorityOwned = minorityOwned === null ? undefined : minorityOwned;

      const result: PreferencesQuizResult = {
        social: {
          pronouns: pronouns || undefined,
          pets: normalizedPet ? [normalizedPet] : undefined,
          lgbtqPositive: lgbtqPositive ?? undefined,
          womenOwned: womenOwned ?? undefined,
          minorityOwned: normalizedMinorityOwned,
          preferToWorkWithRace: preferToWorkWithRace || undefined,
          politicalAffiliation: politicalAffiliation || undefined,
          otherPoliticalAffiliation: (politicalAffiliation === 'other' && otherPoliticalAffiliation) ? otherPoliticalAffiliation : undefined,
          preferSamePolitics: preferSamePolitics ?? undefined
        },
        partnershipStyleRankings: partnershipStylePreferences.map((style, index) => ({
          partnershipStyle: style,
          ranking: index + 1
        })),
        acquisitionPreferences,
        acquisitionRankings: acquisitionPreferences.map((pref, index) => ({
          acquisitionPreference: pref,
          ranking: index + 1
        })),
        ageRange: ageRange || undefined,
        children: hasChildren,
        zipCodes: zipEntriesToStrings(zipEntries)
      };
      onResultChange(result);
    }
  }, [pronouns, selectedPet, lgbtqPositive, womenOwned, minorityOwned, preferToWorkWithRace, politicalAffiliation, otherPoliticalAffiliation, preferSamePolitics, partnershipStylePreferences, acquisitionPreferences, ageRange, hasChildren, zipEntries, zipEntriesToStrings, showBegin, onResultChange]);

  const handlePetChange = (pet: string) => {
    const normalized = normalizePetSelection(pet);
    setSelectedPet(prev => prev === normalized ? undefined : normalized);
  };

  const togglePartnershipStyle = (style: string) => {
    setPartnershipStylePreferences(prev => 
      prev.includes(style) 
        ? prev.filter(s => s !== style)
        : [...prev, style]
    );
  };

  const toggleAcquisitionPreference = (pref: string) => {
    setAcquisitionPreferences(prev => 
      prev.includes(pref) 
        ? prev.filter(p => p !== pref)
        : [...prev, pref]
    );
  };

// Show Begin screen
  if (showBegin) {
    if (onBeginShown) onBeginShown();
    
    return (
      <Container maxWidth="md" sx={{ pl: 2, pr: 2, pt: 6 }}>
        <Box textAlign="center" pt={0} pb={6}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
            Designer Preferences
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Help us understand your preferences for working with clients. This includes social values, working style preferences, and demographic considerations.
          </Typography>
          <Button 
            variant="outlined" 
            size="large" 
            color="secondary"
            endIcon={<ChevronRight sx={{ width: 16, height: 16 }} />}
            onClick={handleBegin}
          >
            Begin
          </Button>
        </Box>
      </Container>
    );
  }

  const renderStep1 = () => (
    <Container maxWidth="md" sx={{ pl: 2, pr: 2, pt: 6 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
          Tell Us About Yourself
        </Typography>
      </Box>

      <Stack spacing={4}>
        <FormControl component="fieldset">
          <Typography variant="body1" fontWeight="bold" gutterBottom>Preferred Pronouns</Typography>
          <RadioGroup
            row
            value={pronouns}
            onChange={(e) => setPronouns(e.target.value)}
          >
            {pronounOptions.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        </FormControl>

        <Box>
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <Typography variant="body1" fontWeight="bold">
              Some clients prefer designers who understand life with pets. While this isn't required, if you have experience living with pets, let us know — it can help us improve matches where relevant.
            </Typography>
            <Tooltip 
              title="We use this info only when a homeowner specifically asks for a designer who understands pet-friendly living. It won't affect your visibility otherwise."
              arrow
              placement="top"
            >
              <IconButton size="small" sx={{ color: 'text.secondary' }}>
                <InfoOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Typography variant="body1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
            Which of the following best describes your current or previous pet experience?
          </Typography>
          <Box maxWidth="800px" mx="auto">
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={selectedPet || ''}
                onChange={(e) => handlePetChange(e.target.value)}
              >
                <Stack spacing={1.5}>
                  {petOptions.map((pet) => (
                    <FormControlLabel
                      key={pet.value}
                      value={pet.value}
                      control={<Radio />}
                      label={pet.label}
                      sx={{
                        alignItems: 'center',
                        '& .MuiFormControlLabel-label': {
                          fontSize: '0.95rem',
                          lineHeight: 1.5
                        }
                      }}
                    />
                  ))}
                </Stack>
              </RadioGroup>
            </FormControl>
          </Box>
        </Box>

        <Box>
          <Typography variant="body1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
            My business is LGBTQ positive.
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              row
              value={lgbtqPositive === null ? '' : lgbtqPositive ? 'yes' : 'no'}
              onChange={(e) => setLgbtqPositive(e.target.value === 'yes')}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box>
          <Typography variant="body1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
            My business is women-owned.
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              row
              value={womenOwned === null ? '' : womenOwned ? 'yes' : 'no'}
              onChange={(e) => setWomenOwned(e.target.value === 'yes')}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box>
          <Typography variant="body1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
            My business is minority-owned.
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              row
              value={
                minorityOwned === null
                  ? ''
                  : minorityOwned
                  ? 'yes'
                  : 'no'
              }
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'yes') setMinorityOwned(true);
                else if (value === 'no') setMinorityOwned(false);
                else setMinorityOwned(null);
              }}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
              <FormControlLabel value="" control={<Radio />} label="Prefer not to say" />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box>
          <Typography variant="body1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
            What's your race?
          </Typography>
          <FormControl sx={{ maxWidth: '414px', width: '100%' }}>
            <Select
              value={preferToWorkWithRace}
              onChange={(e) => setPreferToWorkWithRace(e.target.value)}
              displayEmpty
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E91E63'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E91E63'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E91E63'
                }
              }}
            >
              {racePreferenceOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box>
          <Typography variant="body1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
            Some clients want to work with designers who have the same political affiliations as them, please tell us your political affiliation.
          </Typography>
          <FormControl sx={{ maxWidth: '414px', width: '100%' }}>
            <Select
              value={politicalAffiliation}
              onChange={(e) => {
                setPoliticalAffiliation(e.target.value);
                if (e.target.value !== 'other') {
                  setOtherPoliticalAffiliation('');
                }
              }}
              displayEmpty
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E91E63'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E91E63'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E91E63'
                }
              }}
            >
              {politicalAffiliations.map((affiliation) => (
                <MenuItem key={affiliation.value} value={affiliation.value}>
                  {affiliation.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {politicalAffiliation === 'other' && (
            <TextField
              sx={{ mt: 2, maxWidth: '414px', width: '100%' }}
              label="Please specify"
              value={otherPoliticalAffiliation}
              onChange={(e) => setOtherPoliticalAffiliation(e.target.value)}
            />
          )}
        </Box>

        <Box>
          <Typography variant="body1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
            When it comes to politics, do you want to be matched with clients who have the same political views as you do?
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <FormControl component="fieldset">
            <RadioGroup
              row
                value={preferSamePolitics === null ? 'noPreference' : preferSamePolitics === true ? 'yes' : 'no'}
                onChange={(e) => {
                  if (e.target.value === 'yes') {
                    setPreferSamePolitics(true);
                  } else if (e.target.value === 'no') {
                    setPreferSamePolitics(false);
                  } else {
                    setPreferSamePolitics(null);
                  }
                }}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
                <FormControlLabel value="noPreference" control={<Radio />} label="No Preference" />
            </RadioGroup>
          </FormControl>
          {preferSamePolitics === true && (
              <FormControl sx={{ maxWidth: '414px', width: '100%' }}>
              <Select
                value={politicalAffiliation}
                onChange={(e) => {
                  setPoliticalAffiliation(e.target.value);
                  if (e.target.value !== 'other') {
                    setOtherPoliticalAffiliation('');
                  }
                }}
                displayEmpty
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E91E63'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E91E63'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E91E63'
                  }
                }}
              >
                {politicalAffiliations.map((affiliation) => (
                  <MenuItem key={affiliation.value} value={affiliation.value}>
                    {affiliation.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          </Stack>
        </Box>
      </Stack>
    </Container>
  );


  const renderStep2 = () => (
    <Container maxWidth="md" sx={{ pl: 2, pr: 2, pt: 6 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
          Partnership Style Preferences
        </Typography>
        <Typography variant="body1" color="text.secondary">
          (Select All That Apply)
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {partnershipStyles.map((style) => (
          <Grid item xs={12} sm={6} md={4} key={style.value}>
            <Card
              onClick={() => togglePartnershipStyle(style.value)}
              variant={partnershipStylePreferences.includes(style.value) ? 'elevation' : 'outlined'}
              sx={{
                cursor: 'pointer',
                height: '100%',
                minHeight: '180px',
                boxShadow: 'none',
                border: partnershipStylePreferences.includes(style.value) 
                  ? '2px solid' 
                  : '1px solid',
                borderColor: partnershipStylePreferences.includes(style.value) 
                  ? 'secondary.main' 
                  : 'divider',
                backgroundColor: partnershipStylePreferences.includes(style.value)
                  ? 'secondary.main'
                  : 'transparent',
                '&:hover': {
                  borderColor: 'secondary.main',
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 1.5,
                    fontWeight: 'bold',
                    color: partnershipStylePreferences.includes(style.value) ? '#FFFFFF' : 'secondary.main',
                    textAlign: 'left'
                  }}
                >
                  {style.label}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: partnershipStylePreferences.includes(style.value) ? '#FFFFFF' : 'text.secondary',
                    textAlign: 'left',
                    fontWeight: 'normal'
                  }}
                >
                  {style.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );

  const renderStep3 = () => (
    <Container maxWidth="md" sx={{ pl: 2, pr: 2, pt: 6 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
          Furniture Acquisition Preferences
        </Typography>
        <Typography variant="body1" color="text.secondary">
          (Select All That Apply)
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {acquisitionOptions.map((option) => (
          <Grid item xs={12} sm={6} md={4} key={option.value}>
            <Card
              onClick={() => toggleAcquisitionPreference(option.value)}
              variant={acquisitionPreferences.includes(option.value) ? 'elevation' : 'outlined'}
              sx={{
                cursor: 'pointer',
                height: '100%',
                minHeight: '180px',
                boxShadow: 'none',
                border: acquisitionPreferences.includes(option.value) 
                  ? '2px solid' 
                  : '1px solid',
                borderColor: acquisitionPreferences.includes(option.value) 
                  ? 'secondary.main' 
                  : 'divider',
                backgroundColor: acquisitionPreferences.includes(option.value)
                  ? 'secondary.main'
                  : 'transparent',
                '&:hover': {
                  borderColor: 'secondary.main',
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 1.5,
                    fontWeight: 'bold',
                    color: acquisitionPreferences.includes(option.value) ? '#FFFFFF' : 'secondary.main',
                    textAlign: 'left'
                  }}
                >
                  {option.label}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: acquisitionPreferences.includes(option.value) ? '#FFFFFF' : 'text.secondary',
                    textAlign: 'left',
                    fontWeight: 'normal'
                  }}
                >
                  {option.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );

  const renderStep4 = () => (
    <Container maxWidth="md" sx={{ pl: 2, pr: 2, pt: 6 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' } }}>
          Age Range
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Some homeowners prefer to work with designers in their same age range. Please tell us your age.
        </Typography>
      </Box>

      <Stack spacing={4}>
        <FormControl component="fieldset">
          <RadioGroup
            value={ageRange}
            onChange={(e) => setAgeRange(e.target.value)}
          >
            <Grid container>
              {ageRanges.map((range) => (
                <Grid item xs={6} sm={4} key={range.value}>
                  <FormControlLabel
                    value={range.value}
                    control={<Radio />}
                    label={range.label}
                  />
                </Grid>
              ))}
            </Grid>
          </RadioGroup>
        </FormControl>

        <FormControl component="fieldset">
          <Typography variant="body1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
            Some homeowners prefer to work with designers who have children. Do you have children?
          </Typography>
          <RadioGroup
            row
            value={hasChildren === true ? 'yes' : hasChildren === false ? 'no' : ''}
            onChange={(e) => setHasChildren(e.target.value === 'yes')}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>
      </Stack>
    </Container>
  );

  const handleAddZipCode = (opt: DmaZipOption) => {
    if (zipEntries.length >= 5) {
      alert('You can add up to 5 zip codes.');
      return;
    }
    const exists = zipEntries.some((entry) => entry.zip.trim().toLowerCase() === opt.zip.trim().toLowerCase());
    if (exists) return;
    const meta = zipCityMap[opt.zip];
    const city = meta?.cityOptions?.[0];
    setZipEntries([...zipEntries, { zip: opt.zip, city, cityOptions: meta?.cityOptions, dma: opt.dma }]);
    if (!meta?.cityOptions) {
      fetchCitiesForZip(opt.zip, opt.dma);
    }
  };

  const handleRemoveZipCode = (index: number) => {
    const next = [...zipEntries];
    next.splice(index, 1);
    setZipEntries(next);
  };

  const handleCitySelect = (zip: string, city: string) => {
    setZipEntries((prev) =>
      prev.map((entry) =>
        entry.zip === zip
          ? { ...entry, city }
          : entry
      )
    );
  };

  const renderStep5 = () => (
    <Container maxWidth="md" sx={{ pl: 2, pr: 2, pt: 6 }}>
      <Box mb={1.5}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '20.3px', sm: '1.5rem' }, textAlign: 'left' }}>
          Please identify zip code preferences for:
        </Typography>
        {/* <Typography variant="body1" color="text.secondary">
          Please identify zip code preferences within your assigned DMA(s) so we can route the right projects.
        </Typography> */}
      </Box>

      {assignedDmas.length > 0 && (
        // <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 1.5, marginBottom: 3 }}>
          <LocationOnOutlined color="primary" />
          <div>
            {/* <Typography variant="subtitle2" fontWeight={700}>
              {assignedDmas.length === 1 ? 'Assigned DMA' : 'Assigned DMAs'}
            </Typography> */}
            <Typography variant="body2" color="text.secondary">
              {assignedDmas.map((dma: string, idx: number) => (
                <Typography key={dma} component="span" fontWeight={600} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  {idx > 0 ? ', ' : ''}
                  {formatDmaLabel(dma)} <Box component="span" sx={{ color: 'text.secondary' }}>({dma})</Box>
                </Typography>
              ))}
            </Typography>
          </div>
          </div>
        // </Paper>
      )}

      <br/><br/>

      {assignedDmas.length === 0 && !designerDmaLoading && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          We could not find any assigned DMAs for your account. Please contact support to add your territory.
        </Alert>
      )}

      {assignedDmas.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
          <Stack direction="row" spacing={1} style={{ marginBottom: 1.5 }} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
            {zipEntries.map((entry, index) => (
              <Chip 
                key={`${entry.zip}-${index}`} 
                label={entry.city ? `${entry.city} (${entry.zip})` : entry.zip}
                onDelete={() => handleRemoveZipCode(index)} 
                sx={{ 
                  py: 0.5,
                  px: 0.5,
                  minWidth: 'auto',
                  width: 'fit-content',
                  borderRadius: 50 
                }} 
              />
            ))}
          </Stack>
          <br/><br/>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Search by City and State or Zipcode"
              value={zipCodeInput}
              onChange={(e) => setZipCodeInput(e.target.value)}
              placeholder="Search within your DMA"
              size="small"
              InputProps={{
                startAdornment: <SearchAdornment />,
              }}
            />
            <Divider />
            <Box sx={{ maxHeight: 360, overflowY: 'auto', pr: 1 }}>
              <Grid container sx={{ py: 1, px: 1 }} spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary">
                    City
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary">
                    Zip Codes
                  </Typography>
                </Grid>
                <Grid item xs={3} />
              </Grid>
              <Divider />
              {visibleOptions.map((opt) => {
                const meta = zipCityMap[opt.zip];
                const city = meta?.cityOptions?.[0] || opt.city;
                const status = zipLookupStatus[opt.zip];
                const cityLabel =
                  city ||
                  (status === 'error'
                    ? 'City unavailable'
                    : 'Looking up city…');
                const isSelected = zipEntries.some((entry) => entry.zip === opt.zip);
                return (
                  <Grid
                    key={`${opt.dma}-${opt.zip}`}
                    container
                    alignItems="center"
                    spacing={2}
                    sx={{ py: 1, px: 1, borderBottom: '1px solid #f5f5f5' }}
                  >
                    <Grid item xs={6}>
                      <Typography variant="body1" fontWeight={600}>
                        {cityLabel}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body1" color="text.primary">
                        {opt.zip}
                      </Typography>
                    </Grid>
                    <Grid item xs={3} sx={{ textAlign: 'right' }}>
                      <Button
                        size="small"
                        variant={isSelected ? 'outlined' : 'contained'}
                        color={isSelected ? 'inherit' : 'primary'}
                        onClick={() => handleAddZipCode(opt)}
                        disabled={isSelected || zipEntries.length >= 5}
                        sx={{
                          borderRadius: 999,
                          px: 1.75,
                          textTransform: 'none',
                          minWidth: 96,
                          height: 36,
                          fontSize: '12px!important',
                        }}
                      >
                        {isSelected ? 'Added' : 'Add Zip Code'}
                      </Button>
                    </Grid>
                  </Grid>
                );
              })}
              {filteredOptions.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, px: 1 }}>
                  No results match your search.
                </Typography>
              )}
            </Box>
          </Stack>
        </Paper>
      )}

    </Container>
  );

  return (
    <Box sx={{ pb: 10 }}>
      <Box>
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
        {currentStep === 5 && renderStep5()}
      </Box>
    </Box>
  );
});

PreferencesQuiz.displayName = 'PreferencesQuiz';

export default PreferencesQuiz;